import type { CropData } from '@/types';
import { PRINT_RATIO_CONFIGS } from '@/types';

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const JPEG_QUALITY = 0.95;

export class AssetGenerator {
  validateImage(file: File): { success: boolean; error?: string } {
    if (!file.type.startsWith('image/')) {
      return { success: false, error: 'File must be an image' };
    }

    if (file.size > MAX_FILE_SIZE) {
      return { success: false, error: 'Image must be less than 50MB' };
    }

    return { success: true };
  }

  async generateCroppedAssets(masterImage: File, crops: CropData[]): Promise<CropData[]> {
    const img = await this.loadImage(masterImage);

    const processedCrops = await Promise.all(
      crops.map(async (crop) => {
        const targetDimensions = PRINT_RATIO_CONFIGS[crop.ratio];
        const orientation = crop.orientation ?? 'portrait';
        
        const canvasWidth = orientation === 'portrait'
          ? targetDimensions.width
          : targetDimensions.height;
        const canvasHeight = orientation === 'portrait'
          ? targetDimensions.height
          : targetDimensions.width;

        const canvas = document.createElement('canvas');
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          throw new Error('Failed to get canvas context');
        }

        ctx.drawImage(
          img,
          crop.x,
          crop.y,
          crop.width,
          crop.height,
          0,
          0,
          canvasWidth,
          canvasHeight
        );

        const blob = await new Promise<Blob>((resolve, reject) => {
          canvas.toBlob(
            (blob) => {
              if (blob) resolve(blob);
              else reject(new Error(`Failed to create blob for ${crop.ratio}`));
            },
            'image/jpeg',
            JPEG_QUALITY
          );
        });

        return { ...crop, blob };
      })
    );

    return processedCrops;
  }

  private loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image'));
      };
      img.src = URL.createObjectURL(file);
    });
  }
}
